import {
  createAction,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { MODAL_STATUS } from 'src/components/Modal/constants';
import { RootState } from 'src/redux/store';
import { PageMetaDataType, UUID } from 'src/types/utility';
import { DATA_FETCHING_STATUS, DATA_STATE_KEY } from '../../constants';
import {
  AddOrEditGroupsProps,
  GetSiteGroupsProps,
} from 'src/redux/data/siteGroups/modules/types';
import { CurrentSiteGroupType, SiteGroupType } from 'src/services/types';
import { DeviceDisconnectionOverview } from 'src/types/devices';

export const STATE_KEY = 'siteGroups';

export const INITIAL_STATE: {
  siteGroups: {
    data: SiteGroupType[];
    pageMetadata: PageMetaDataType;
  };
  currentUserGroups: {
    data: SiteGroupType[];
    status: keyof typeof DATA_FETCHING_STATUS | null;
  };
  siteGroupsTableData: {
    data: SiteGroupType[];
    pageMetadata: PageMetaDataType;
    status: keyof typeof DATA_FETCHING_STATUS;
  };
  currentSiteGroups: {
    data: CurrentSiteGroupType | null;
    status: keyof typeof DATA_FETCHING_STATUS;
  };
  disconnectedDevicesLogs: {
    data: Record<UUID, DeviceDisconnectionOverview[]>;
    apiStatus: keyof typeof DATA_FETCHING_STATUS;
    pageMetadata: Record<UUID, PageMetaDataType>;
  };
  status: keyof typeof DATA_FETCHING_STATUS;
  modalStatus: keyof typeof MODAL_STATUS;
} = {
  siteGroups: {
    data: [],
    pageMetadata: {
      totalResults: 0,
      page: 0,
      limit: 0,
    },
  },
  currentUserGroups: {
    data: [],
    status: null,
  },
  siteGroupsTableData: {
    data: [],
    status: DATA_FETCHING_STATUS.INITIAL,
    pageMetadata: {
      totalResults: 0,
      page: 0,
      limit: 0,
    },
  },
  disconnectedDevicesLogs: {
    data: {},
    apiStatus: DATA_FETCHING_STATUS.INITIAL,
    pageMetadata: {},
  },
  currentSiteGroups: {
    data: null,
    status: DATA_FETCHING_STATUS.INITIAL,
  },
  status: DATA_FETCHING_STATUS.INITIAL,
  modalStatus: MODAL_STATUS.INITIAL,
};

const slice = createSlice({
  name: STATE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setSiteGroups(
      state,
      action: PayloadAction<{
        data: SiteGroupType[];
        pageMetadata: PageMetaDataType;
      }>,
    ) {
      state.siteGroups = { ...action.payload };
    },
    setCurrentUserGroups(state, action: PayloadAction<SiteGroupType[]>) {
      state.currentUserGroups.data = action.payload;
    },
    setCurrentUserGroupsStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.currentUserGroups.status = action.payload;
    },
    setCurrentSiteGroups(
      state,
      action: PayloadAction<{
        data: CurrentSiteGroupType | null;
      }>,
    ) {
      state.currentSiteGroups.data = action.payload.data;
    },
    setSiteGroupsTableData(
      state,
      action: PayloadAction<{
        data: SiteGroupType[];
        pageMetadata: PageMetaDataType;
      }>,
    ) {
      const { data, pageMetadata } = action.payload;
      state.siteGroupsTableData.data = data;
      state.siteGroupsTableData.pageMetadata = pageMetadata;
    },
    setStatus(state, action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>) {
      state.status = action.payload;
    },
    setSiteGroupsTableDataStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.siteGroupsTableData.status = action.payload;
    },
    setCurrentSiteGroupsStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.currentSiteGroups.status = action.payload;
    },
    setModalStatus(state, action: PayloadAction<keyof typeof MODAL_STATUS>) {
      state.modalStatus = action.payload;
    },
    fetchSiteGroupDisconnectionOverviewSuccess: (
      state,
      action: PayloadAction<{
        disconnectedDevices: DeviceDisconnectionOverview[];
        metaData: PageMetaDataType;
        groupId: UUID;
      }>,
    ) => {
      const { disconnectedDevices, metaData, groupId } = action.payload;

      state.disconnectedDevicesLogs.data[groupId] = disconnectedDevices;
      state.disconnectedDevicesLogs.pageMetadata[groupId] = metaData || {};
    },
    fetchSiteGroupDisconnectionOverviewStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.disconnectedDevicesLogs.apiStatus = action.payload;
    },
  },
  extraReducers: {},
});

const getState = (state: RootState) =>
  state[DATA_STATE_KEY][STATE_KEY] || INITIAL_STATE;

const getGroupId = (_state: RootState, groupId: UUID) => groupId;

export const selectors = {
  getCurrentUserGroups: createSelector(
    getState,
    state => state.currentUserGroups.data,
  ),
  getCurrentUserGroupsStatus: createSelector(
    getState,
    state => state.currentUserGroups.status,
  ),
  getSiteGroups: createSelector(getState, state => state.siteGroups),
  getCurrentSiteGroups: createSelector(
    getState,
    state => state.currentSiteGroups.data,
  ),
  getSiteGroupsTableData: createSelector(
    getState,
    state => state.siteGroupsTableData,
  ),
  getModalStatus: createSelector(getState, state => state.modalStatus),
  getSiteGroupsTableDataStatus: createSelector(
    getState,
    state => state.siteGroupsTableData.status,
  ),
  getCurrentSiteGroupsStatus: createSelector(
    getState,
    state => state.currentSiteGroups.status,
  ),
  getStatus: createSelector(getState, state => state.status),
  selectSiteGroupDisconnectionOverview: createSelector(
    getState,
    getGroupId,
    (state, groupId) => state.disconnectedDevicesLogs.data[groupId] || [],
  ),
  selectSiteGroupDisconnectionMetadata: createSelector(
    getState,
    getGroupId,
    (state, groupId) => state.disconnectedDevicesLogs.pageMetadata[groupId],
  ),
  selectSiteGroupDisconnectionStatus: createSelector(
    getState,
    state => state.disconnectedDevicesLogs.apiStatus,
  ),
};

const extraActions = {
  getSiteGroups: createAction<GetSiteGroupsProps>(`${STATE_KEY}/getSiteGroups`),
  getCurrentSiteGroups: createAction<{ id: UUID }>(
    `${STATE_KEY}/getCurrentSiteGroups`,
  ),
  getSiteGroupsTableData: createAction<GetSiteGroupsProps>(
    `${STATE_KEY}/getSiteGroupsTableData`,
  ),
  getCurrentUserGroups: createAction(`${STATE_KEY}/getCurrentUserGroups`),
  deleteSiteGroup: createAction<{
    groupId: UUID;
    page: number;
    limit: number;
    customerID?: UUID;
    searchKeyword: string;
  }>(`${STATE_KEY}/deleteSiteGroup`),
  createSiteGroup: createAction<AddOrEditGroupsProps>(
    `${STATE_KEY}/createSiteGroup`,
  ),
  editSiteGroup: createAction<{
    id: UUID;
    data: AddOrEditGroupsProps;
  }>(`${STATE_KEY}/editSiteGroup`),
  fetchSiteGroupDisconnectionOverview: createAction<{
    groupId: UUID;
    continuousDisconnectionSeconds: number;
    currentPage: number | string;
    pageSize: string | number;
  }>(`${STATE_KEY}/fetchSiteGroupDisconnectionOverview`),
};

export const actions = { ...slice.actions, ...extraActions };

const { reducer } = slice;
export default reducer;
